import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import style from "../scss/EstadoConexion.module.scss";
import useConexion from "../hooks/useConexion";
import { eliminarPendiente, pedirAlmacenamientoPersistente } from "../utils/almacenLocal";
import { TIPOS_PENDIENTE, sincronizarPendientes } from "../utils/colaSincronizacion";

const INTERVALO_REINTENTO = 30000;

const describirPendiente = (pendiente) => {
  if (pendiente.tipo === "elector") {
    const { cedula, nombres, apellidos } = pendiente.datos;
    return `C.C. ${cedula} · ${nombres} ${apellidos}`;
  }
  if (pendiente.tipo === "escrutinio") {
    const { lugar, mesa } = pendiente.datos;
    return `${lugar} · Mesa ${mesa}${pendiente.cierre ? " (cerrada)" : ""}`;
  }
  return "Foto de un acta cerrada";
};

const avisoResultado = ({ enviados, conError, sesionVencida }) => {
  if (sesionVencida) {
    Swal.fire({
      icon: "warning",
      title: "Tu sesión venció",
      text: "Inicia sesión de nuevo para enviar los registros guardados sin conexión. No se perdieron.",
    });
    return;
  }
  if (enviados > 0) {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: `${enviados} ${enviados === 1 ? "registro enviado" : "registros enviados"} al servidor`,
      showConfirmButton: false,
      timer: 3000,
    });
  }
  if (conError > 0) {
    Swal.fire({
      icon: "warning",
      text: `${conError} ${conError === 1 ? "registro fue rechazado" : "registros fueron rechazados"} por el servidor. Revísalo en el indicador de registros (abajo a la derecha).`,
    });
  }
};

// Indicador flotante del modo sin conexión: muestra si no hay internet,
// cuántos registros esperan enviarse y los que el servidor rechazó.
// Además dispara la sincronización: al abrir la app, al volver la señal
// y cada 30 s mientras haya pendientes.
const EstadoConexionComponent = () => {
  const { enLinea, sincronizando, porEnviar, conError } = useConexion();
  const [mostrarDetalle, setMostrarDetalle] = useState(false);

  const sincronizar = useCallback((opciones) => {
    sincronizarPendientes(opciones)
      .then(avisoResultado)
      .catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    pedirAlmacenamientoPersistente();
    sincronizar();
    const alConectar = () => sincronizar();
    window.addEventListener("online", alConectar);
    return () => window.removeEventListener("online", alConectar);
  }, [sincronizar]);

  const hayPorEnviar = porEnviar.length > 0;
  useEffect(() => {
    if (!hayPorEnviar) return undefined;
    const intervalo = setInterval(() => navigator.onLine && sincronizar(), INTERVALO_REINTENTO);
    return () => clearInterval(intervalo);
  }, [hayPorEnviar, sincronizar]);

  const descartar = async (pendiente) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "¿Descartar este registro?",
      text: `${describirPendiente(pendiente)}. Se borrará de este equipo y no se enviará.`,
      showCancelButton: true,
      confirmButtonText: "Sí, descartar",
      cancelButtonText: "Cancelar",
    });
    if (isConfirmed) await eliminarPendiente(pendiente.id);
  };

  if (enLinea && !sincronizando && !hayPorEnviar && conError.length === 0) return null;

  const estadoVisual = !enLinea ? style.sinConexion : conError.length > 0 ? style.conError : style.porEnviar;
  const pendientesTotales = porEnviar.length + conError.length;

  return (
    <>
      <div className={`${style.indicador} ${estadoVisual}`} role="status" aria-live="polite">
        <Icon
          icon={
            !enLinea
              ? "mdi:wifi-off"
              : sincronizando
              ? "mdi:cloud-sync-outline"
              : conError.length > 0
              ? "mdi:cloud-alert-outline"
              : "mdi:cloud-upload-outline"
          }
          className={`${style.icono} ${sincronizando ? style.girando : ""}`}
          aria-hidden="true"
        />
        <span className={style.texto}>
          <strong>
            {!enLinea
              ? "Sin conexión"
              : sincronizando
              ? "Sincronizando…"
              : hayPorEnviar
              ? "Registros por enviar"
              : "Registros rechazados"}
          </strong>
          <small>
            {pendientesTotales === 0
              ? "Puedes seguir registrando: se guarda en este equipo"
              : `${pendientesTotales} ${pendientesTotales === 1 ? "registro guardado" : "registros guardados"} en este equipo`}
            {conError.length > 0 && ` · ${conError.length} con error`}
          </small>
        </span>
        {pendientesTotales > 0 && (
          <button type="button" className={style.boton} onClick={() => setMostrarDetalle(true)}>
            Ver
          </button>
        )}
      </div>

      <Modal show={mostrarDetalle} onHide={() => setMostrarDetalle(false)} centered scrollable enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className={style.modalTitulo}>
            Registros guardados en este equipo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className={style.ayuda}>
            {enLinea
              ? "Se envían automáticamente. Puedes forzar el envío ahora."
              : "Sin conexión: se enviarán automáticamente cuando vuelva la señal. No cierres sesión."}
          </p>
          <ul className={style.lista}>
            {[...conError, ...porEnviar].map((pendiente) => (
              <li key={pendiente.id} className={`${style.item} ${pendiente.estado === "error" ? style.itemError : ""}`}>
                <div className={style.itemTexto}>
                  <span className={style.itemTipo}>{TIPOS_PENDIENTE[pendiente.tipo]}</span>
                  <span className={style.itemDetalle}>{describirPendiente(pendiente)}</span>
                  <span className={style.itemFecha}>
                    Guardado {new Date(pendiente.actualizadoEn).toLocaleString("es-CO")}
                  </span>
                  {pendiente.error && (
                    <span className={style.itemMensaje}>
                      <Icon icon="mdi:alert-circle-outline" aria-hidden="true" /> {pendiente.error}
                    </span>
                  )}
                </div>
                {pendiente.estado === "error" && (
                  <button
                    type="button"
                    className={style.descartar}
                    onClick={() => descartar(pendiente)}
                    aria-label={`Descartar ${describirPendiente(pendiente)}`}
                    title="Descartar"
                  >
                    <Icon icon="mdi:trash-can-outline" aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className={`btn ${style.enviar}`}
            onClick={() => sincronizar({ incluirErrores: true })}
            disabled={!enLinea || sincronizando}
          >
            <Icon icon="mdi:cloud-upload-outline" aria-hidden="true" />
            {sincronizando ? "Enviando…" : "Enviar ahora"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EstadoConexionComponent;
